/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import React, { memo, useEffect, useLayoutEffect, useState } from 'react';
import { Form, message, Row, Tabs } from 'antd';

import Utils from '../../Assets/Scripts/Utils';
import { api } from '../../Services/axiosService';
import { deleteFileDocuments, uploadFiles } from '../../Services/firebaseService';

import { getDocumentTypes, getSettings } from './API/SettingsAPI';
import CertificateFieldsModal from './Components/CertificateFieldsModal';
import CertificateSettings from './Components/CertificateSettings';
import EmailSettings from './Components/EmailSettings';
import ExternalApisSettings from './Components/ExternalApisSettings';
import HolidaySettings from './Components/HolidaySettings';
import RequiredDocumentsSettings from './Components/RequiredDocumentsSettings';
import ScheduleSettings from './Components/ScheduleSettings';
import SettingsToolbar from './Components/SettingsToolbar';
import TrackingSettings from './Components/TrackingSettings';

import './Settings.scss';

function SettingsPage() {
  const [form] = Form.useForm();
  const [formHoliday] = Form.useForm();
  const [formUploadFile] = Form.useForm();

  const [screenModified, setScreenModified] = useState(false);
  const [fieldsPermissions, setFieldsPermissions] = useState();
  const [loading, setLoading] = useState(false);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [emailBlacklist, setEmailBlacklist] = useState([]);
  const [newHolidayList, setNewHolidayList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [submitFileList, setSubmitFileList] = useState([]);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editValue, setEditValue] = useState('');
  const [isCertificateModalVisible, setIsCertificateModalVisible] = useState(false);

  const handleCertificateModelUploadChange = (info) => {
    const newFileList = info.fileList.map((fileProps) => ({ ...fileProps, status: 'done' }));
    setFileList(newFileList);
  };

  const handleCertificateModelStartEdit = (index, currentName) => {
    setEditingIndex(index);
    setEditValue(currentName);
  };

  const handleCertificateModelCancelEdit = () => {
    setEditingIndex(-1);
    setEditValue('');
  };

  const handleCertificateModelEdit = (index) => {
    const newList = [...submitFileList];
    newList[index].label = editValue;
    setSubmitFileList(newList);
    handleCertificateModelCancelEdit();
  };

  const handleCertificateModel = async () => {
    try {
      const values = await formUploadFile.validateFields();
      const mappedFiles = [];
      for (const file of fileList) {
        const fileExtension = file.name.split('.').pop();
        const fileName = `${values.certificateModelFileName}_${Date.now()}.${fileExtension}`;
        mappedFiles.push({
          ...file,
          id: Utils.generateUUID(),
          label: values.certificateModelFileName,
          name: fileName,
          url: null,
          modelType: values.certificateModelType,
        });
      }
      setSubmitFileList((prevList) => [...prevList, ...mappedFiles]);
      formUploadFile.resetFields();
      setFileList([]);
    } catch (error) {
      message.error(`Erro ao processar o formulário: ${error.message}`);
    }
  };

  const submitHoliday = (values) => {
    const { holidayDate, holidayName, holidayLevel, holidayType } = values;
    newHolidayList.push({
      date: holidayDate,
      name: holidayName,
      level: holidayLevel,
      type: holidayType,
      law: null,
    });
    setNewHolidayList([...newHolidayList]);
    formHoliday.resetFields();
  };

  const submit = async (values, newList) => {
    setLoading(true);
    const data = { ...values };

    if (data.requiredDocumentsCT?.length > 0) {
      data.requiredDocumentsCT = data.requiredDocumentsCT.join(',');
    } else {
      data.requiredDocumentsCT = null;
    }

    if (data.requiredDocumentsInstructor?.length > 0) {
      data.requiredDocumentsInstructor = data.requiredDocumentsInstructor.join(',');
    } else {
      data.requiredDocumentsInstructor = null;
    }

    if (data.requiredDocumentsDriver?.length > 0) {
      data.requiredDocumentsDriver = data.requiredDocumentsDriver.join(',');
    } else {
      data.requiredDocumentsDriver = null;
    }

    if (data.requiredDocumentsTransport?.length > 0) {
      data.requiredDocumentsTransport = data.requiredDocumentsTransport.join(',');
    } else {
      data.requiredDocumentsTransport = null;
    }

    if (data.requiredDocumentsVehicle?.length > 0) {
      data.requiredDocumentsVehicle = data.requiredDocumentsVehicle.join(',');
    } else {
      data.requiredDocumentsVehicle = null;
    }

    const certificateList = newList || submitFileList;
    if (certificateList.length > 0) {
      for (let index = 0; index < certificateList.length; index += 1) {
        const file = certificateList[index];
        if (!file.url) {
          const downloadUrl = await uploadFiles(file.originFileObj, 'settings', 1, file.name);
          file.url = downloadUrl;
        }
      }
      data.certificateModelList = certificateList;
    } else {
      data.certificateModelList = null;
    }

    data.holidayList = newHolidayList;
    data.emailBlacklist = emailBlacklist;
    try {
      await api.put('/Settings', data);
      message.success('Dados atualizados!');
    } catch (error) {
      Utils.logError(error);
      message.error('Oops. Algo deu errado ao atualizar os dados!');
    } finally {
      setLoading(false);
    }
  };

  const handleCertificateModelDelete = async (file, index) => {
    try {
      const { label, name, url } = file;
      const clearList = () => {
        const newList = [...submitFileList];
        newList.splice(index, 1);
        setSubmitFileList(newList);
        return newList;
      };
      if (url) {
        await deleteFileDocuments(name, 'settings', 1);
        const newList = clearList();
        await submit(form.getFieldsValue(), newList);
      }
      clearList();
      message.success(`Modelo "${label}" removido com sucesso.`);
    } catch (error) {
      message.error('Oops. Ocorreu um erro ao tentar remover o arquivo!');
    }
  };

  const handleImport = async (file) => {
    if (file.status !== 'removed') {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
    }
  };

  const fetchData = async () => {
    const [documentTypes, settings] = await Promise.all([getDocumentTypes(), getSettings()]);
    setSubmitFileList(settings.certificateModelList ?? []);

    form.setFieldsValue(settings);

    setEmailBlacklist(settings?.emailBlacklist ?? []);
    setNewHolidayList(settings?.holidayList ?? []);
    setDocumentTypeOptions(documentTypes);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (fileList.length > 0) {
      const latestFile = fileList[fileList.length - 1];
      const fileNameWithoutExtension = latestFile.name.replace(/\.[^/.]+$/, '');
      formUploadFile.setFieldsValue({ certificateModelFileName: fileNameWithoutExtension });
    }
  }, [fileList, formUploadFile]);

  useLayoutEffect(() => {
    const permissions = {};
    JSON.parse(localStorage.getItem('conecta__permissions'))?.resources.forEach(
      ({ name, fields }) => {
        if (name === 'Configuration') {
          fields.forEach(({ name, access, isRequired }) => {
            permissions[name] = { access, isRequired };
          });
        }
      }
    );
    setFieldsPermissions(permissions);
  }, []);

  return (
    <>
      <Form
        form={form}
        disabled={loading}
        name="settingsForm"
        layout="vertical"
        onFinish={submit}
        autoComplete="off"
        onChange={() => setScreenModified(true)}
      >
        <SettingsToolbar screenModified={screenModified} loading={loading} />
        <Tabs defaultActiveKey="email" style={{ marginTop: 16 }}>
          <Tabs.TabPane tab="E-mail" key="email" forceRender>
            <Row gutter={[24]}>
              <EmailSettings
                fieldsPermissions={fieldsPermissions}
                emailBlacklist={emailBlacklist}
                fetchData={fetchData}
              />
            </Row>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Agenda" key="agenda" forceRender>
            <Row gutter={[24]}>
              <ScheduleSettings fieldsPermissions={fieldsPermissions} />
            </Row>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Rastreamento do Treinamento" key="tracking" forceRender>
            <Row gutter={[24]}>
              <TrackingSettings fieldsPermissions={fieldsPermissions} />
            </Row>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Integrações" key="integrations" forceRender>
            <Row gutter={[24]}>
              <ExternalApisSettings fieldsPermissions={fieldsPermissions} />
            </Row>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Documentos" key="documents" forceRender>
            <Row gutter={[24]}>
              <RequiredDocumentsSettings
                fieldsPermissions={fieldsPermissions}
                documentTypeOptions={documentTypeOptions}
              />
            </Row>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Feriados" key="holidays" forceRender>
            <HolidaySettings
              loading={loading}
              newHolidayList={newHolidayList}
              submitHoliday={submitHoliday}
              setScreenModified={setScreenModified}
              formHoliday={formHoliday}
              fieldsPermissions={fieldsPermissions}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="Modelos de Certificado" key="certificates" forceRender>
            <CertificateSettings
              loading={loading}
              fileList={fileList}
              submitFileList={submitFileList}
              editingIndex={editingIndex}
              editValue={editValue}
              setEditValue={setEditValue}
              setScreenModified={setScreenModified}
              handleCertificateModelUploadChange={handleCertificateModelUploadChange}
              handleCertificateModelStartEdit={handleCertificateModelStartEdit}
              handleCertificateModelCancelEdit={handleCertificateModelCancelEdit}
              handleCertificateModelEdit={handleCertificateModelEdit}
              handleCertificateModel={handleCertificateModel}
              handleCertificateModelDelete={handleCertificateModelDelete}
              handleImport={handleImport}
              setIsCertificateModalVisible={setIsCertificateModalVisible}
              formUploadFile={formUploadFile}
              fieldsPermissions={fieldsPermissions}
            />
          </Tabs.TabPane>
        </Tabs>
      </Form>
      {isCertificateModalVisible && (
        <CertificateFieldsModal setIsVisible={setIsCertificateModalVisible} />
      )}
    </>
  );
}

const Settings = memo(SettingsPage);
export default Settings;
